import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import classnames from 'classnames';
import * as Yup from "yup";
import { useFormik } from "formik";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";
import {getUserDetails, updateUser} from "../../slices/user/details/thunk";
import {validators} from "../../helpers/ValidatorHelper";
import {
  fetchUserTips,
  markTipsPaid,
  setTipStatus,
} from "../../slices/tip/userTips/thunk";
import {
  clearAdminUserTips,
  selectAdminUserTips,
  selectAdminUserTipsError,
  selectAdminUserTipsLoading,
  selectAdminUserTipsUpdating,
} from "../../slices/tip/userTips/reducer";
import {authUserManager} from "../../helpers/api_helper";
import {regenerateUserQrCode, setUserEnabled} from "../../helpers/user_helper";
import {notificationService} from "../../helpers/NotificationService";

const STATUS_UNPAID = 0;
const STATUS_PAID = 1;

const formatAmount = (amount) => `${amount ?? 0} PLN`;

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const User = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = authUserManager.isAdmin();

  const selectEventDetails = createSelector(
    (state) => state.User,
    (slice) => ({
      username: slice.user.username,
      email: slice.user.email,
      // Kod QR jest przypisany do wydarzenia club admina, nie do samego usera.
      eventId: slice.user.event?.id || null,
      roles: slice.user.roles || [],
      enabled: slice.user.enabled,
    })
  );
  const {
    username,
    email,
    eventId,
    roles,
    enabled,
  } = useSelector(selectEventDetails);

  document.title = t('User') + ' - ' + username

  const [activeTab, setActiveTab] = useState('1');

  const editUserForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: username || '',
      email: email || '',
      plainPassword: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().required(t("Please Enter Email"))
        .min(7, t('At least 7 chars'))
        .max(40, t('Up to 30 chars')),
      username: validators.username(t),
      plainPassword: Yup.string()
        .min(8, 'Hasło musi mieć minimum 8 znaków')
        .max(20, 'Hasło może mieć maksymalnie 20 znaków')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
          'Hasło musi zawierać: małą literę, wielką literę, cyfrę i znak specjalny'
        )
    }),
    onSubmit: async (values) => {
      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== '' && value != null)
      );
      dispatch(updateUser(userId, filteredValues));
    }
  });

  useEffect(() => {
    dispatch(getUserDetails(userId));
  }, [userId]);

  //BLOKADA KONTA
  const isClubAdminUser = roles.includes('ROLE_CLUB_ADMIN');
  // Główny admin nie może zablokować sam siebie — API i tak to odrzuca (400).
  const isSelf = authUserManager.getId() === userId;
  // Lokalny stan, bo odpowiedź endpointu nie przechodzi przez slice usera.
  const [accountEnabled, setAccountEnabled] = useState(true);
  const [enabledSaving, setEnabledSaving] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);

  useEffect(() => {
    if (enabled !== undefined) setAccountEnabled(enabled);
  }, [enabled]);

  const handleSetEnabled = async (nextEnabled) => {
    setConfirmBlock(false);
    setEnabledSaving(true);
    try {
      const response = await setUserEnabled(userId, nextEnabled);
      setAccountEnabled(response?.enabled ?? nextEnabled);
      notificationService.success(nextEnabled ? t('account.unblockedToast') : t('account.blockedToast'));
    } catch (e) {
      notificationService.error(t('account.changeFailed'));
    } finally {
      setEnabledSaving(false);
    }
  };

  //QR CODE
  const [qrRegenerating, setQrRegenerating] = useState(false);
  // Podbijany po regeneracji: bez tego przeglądarka pokazuje stary plik z cache.
  const [qrVersion, setQrVersion] = useState(0);
  const [confirmRegenerateQr, setConfirmRegenerateQr] = useState(false);

  const qrSrc = eventId
    ? `${process.env.REACT_APP_API_PUBLIC_URL.replace(/\/$/, '')}/qr/qr_${eventId}.svg?v=${qrVersion}`
    : null;
  const qrEventUrl = eventId ? `${window.location.origin}/event/${eventId}` : null;

  const handleRegenerateQr = async () => {
    setConfirmRegenerateQr(false);
    setQrRegenerating(true);
    try {
      await regenerateUserQrCode(userId);
      setQrVersion((v) => v + 1);
      notificationService.success(t('qrCode.regenerated'));
    } catch (e) {
      notificationService.error(t('qrCode.regenerateFailed'));
    } finally {
      setQrRegenerating(false);
    }
  };

  const handleDownloadQr = () => {
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_API_URL}/qr/download/qr_${eventId}.svg`;
    link.download = `qr_${eventId}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //TIPS
  const tips = useSelector(selectAdminUserTips);
  const tipsLoading = useSelector(selectAdminUserTipsLoading);
  const tipsUpdating = useSelector(selectAdminUserTipsUpdating);
  const tipsError = useSelector(selectAdminUserTipsError);

  const [selectedTipIds, setSelectedTipIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmMarkAll, setConfirmMarkAll] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchUserTips(userId));
    }
    return () => {
      dispatch(clearAdminUserTips());
    };
  }, [dispatch, userId, isAdmin]);

  // Zaznaczenie dotyczy tylko nierozliczonych napiwków — wypłacone znikają z niego same.
  useEffect(() => {
    setSelectedTipIds((current) =>
      current.filter((id) => tips.some((tip) => tip.id === id && tip.status === STATUS_UNPAID))
    );
  }, [tips]);

  const unpaidTips = useMemo(
    () => tips.filter((tip) => tip.status === STATUS_UNPAID),
    [tips]
  );

  const paidAmount = useMemo(
    () => tips.filter((tip) => tip.status === STATUS_PAID).reduce((sum, tip) => sum + tip.amount, 0),
    [tips]
  );
  const unpaidAmount = useMemo(
    () => unpaidTips.reduce((sum, tip) => sum + tip.amount, 0),
    [unpaidTips]
  );
  const totalAmount = paidAmount + unpaidAmount;

  const filteredTips = useMemo(() => {
    if (statusFilter === '') return tips;
    return tips.filter((tip) => tip.status === Number(statusFilter));
  }, [tips, statusFilter]);

  const toggleTipSelection = (id) => {
    setSelectedTipIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const visibleUnpaidIds = useMemo(
    () => filteredTips.filter((tip) => tip.status === STATUS_UNPAID).map((tip) => tip.id),
    [filteredTips]
  );

  const allVisibleSelected =
    visibleUnpaidIds.length > 0 && visibleUnpaidIds.every((id) => selectedTipIds.includes(id));

  const toggleSelectAllVisible = () => {
    setSelectedTipIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleUnpaidIds.includes(id))
        : Array.from(new Set([...current, ...visibleUnpaidIds]))
    );
  };

  const handleMarkSelectedPaid = () => {
    if (selectedTipIds.length === 0) return;
    dispatch(markTipsPaid({ userId, ids: selectedTipIds }));
  };

  const handleMarkAllPaid = () => {
    dispatch(markTipsPaid({ userId, ids: null }));
    setConfirmMarkAll(false);
  };

  const handleToggleTipStatus = (tip) => {
    dispatch(setTipStatus({
      id: tip.id,
      status: tip.status === STATUS_PAID ? STATUS_UNPAID : STATUS_PAID,
    }));
  };

  const tipColumns = useMemo(
    () => [
      {
        id: 'select',
        enableSorting: false,
        header: () => (
          <Input
            type="checkbox"
            className="form-check-input"
            checked={allVisibleSelected}
            disabled={visibleUnpaidIds.length === 0}
            onChange={toggleSelectAllVisible}
            aria-label={t('adminTips.selectAll')}
          />
        ),
        enableColumnFilter: false,
        cell: ({ row }) => {
          const tip = row.original;
          if (tip.status === STATUS_PAID) return null;
          return (
            <Input
              type="checkbox"
              className="form-check-input"
              checked={selectedTipIds.includes(tip.id)}
              onChange={() => toggleTipSelection(tip.id)}
              aria-label={`${t('adminTips.select')} #${tip.id}`}
            />
          );
        },
      },
      {
        header: t('tips.table.id'),
        accessorKey: 'id',
        enableColumnFilter: false,
        cell: (cell) => <span className="fw-medium">#{cell.getValue()}</span>,
      },
      {
        header: t('tips.table.date'),
        accessorKey: 'createdAt',
        enableColumnFilter: false,
        cell: (cell) => formatDateTime(cell.getValue()),
      },
      {
        header: t('tips.table.username'),
        accessorKey: 'username',
        enableColumnFilter: false,
      },
      {
        header: t('tips.table.amount'),
        accessorKey: 'amount',
        enableColumnFilter: false,
        cell: (cell) => <span className="fw-semibold">{formatAmount(cell.getValue())}</span>,
      },
      {
        header: t('tips.table.status'),
        accessorKey: 'status',
        enableColumnFilter: false,
        cell: (cell) => {
          const isPaid = cell.getValue() === STATUS_PAID;
          return (
            <span className={`badge ${isPaid ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'} text-uppercase`}>
              {isPaid ? t('tips.status.paid') : t('tips.status.unpaid')}
            </span>
          );
        },
      },
      {
        header: t('adminTips.table.event'),
        accessorKey: 'eventName',
        enableColumnFilter: false,
        cell: (cell) => cell.getValue() || '-',
      },
      {
        header: t('adminTips.table.consumed'),
        accessorKey: 'consumed',
        enableColumnFilter: false,
        cell: (cell) => (
          <span className={`badge ${cell.getValue() ? 'bg-info-subtle text-info' : 'bg-light text-muted'}`}>
            {cell.getValue() ? t('adminTips.consumed.yes') : t('adminTips.consumed.no')}
          </span>
        ),
      },
      {
        header: t('adminTips.table.paymentIntent'),
        accessorKey: 'stripePaymentIntentId',
        enableColumnFilter: false,
        cell: (cell) => (
          <span className="font-monospace fs-12">{cell.getValue() || '-'}</span>
        ),
      },
      {
        header: t('adminTips.table.charge'),
        accessorKey: 'stripeChargeId',
        enableColumnFilter: false,
        cell: (cell) => (
          <span className="font-monospace fs-12">{cell.getValue() || '-'}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const tip = row.original;
          const isPaid = tip.status === STATUS_PAID;
          return (
            <Button
              size="sm"
              color={isPaid ? 'light' : 'success'}
              disabled={tipsUpdating}
              onClick={() => handleToggleTipStatus(tip)}
            >
              {isPaid ? t('adminTips.markUnpaid') : t('adminTips.markPaid')}
            </Button>
          );
        },
      },
    ],
    [t, selectedTipIds, allVisibleSelected, visibleUnpaidIds, tipsUpdating]
  );

  //User Edit
  const selectUpdateUser = createSelector(
    (state) => state.User,
    (slice) => ({
      userUpdateSuccess: slice.updateSuccess,
      userUpdateError: slice.violations,
    })
  );
  const {
    userUpdateError,
    userUpdateSuccess,
  } = useSelector(selectUpdateUser);

  useEffect(() => {
    if (userUpdateError?.length > 0) {
      userUpdateError.forEach((violation) => {
        editUserForm.setFieldError(violation.propertyPath, t(violation.message));
      });
    }
  }, [userUpdateError]);


  useEffect(() => {
    if (userUpdateSuccess) {
      editUserForm.resetForm();
      navigate('/admin/user');
    }
  }, [userUpdateSuccess]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={username || t('User')} pageTitle={t('Users')} link="/admin/user" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0 nav-success" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => setActiveTab('1')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="ri-user-3-line me-1"></i>
                        {t('adminTips.tab.user')}
                      </NavLink>
                    </NavItem>
                    {isAdmin && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === '2' })}
                          onClick={() => setActiveTab('2')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="ri-money-dollar-circle-line me-1"></i>
                          {t('adminTips.tab.tips')}
                        </NavLink>
                      </NavItem>
                    )}
                    {isAdmin && isClubAdminUser && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === '3' })}
                          onClick={() => setActiveTab('3')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="ri-qr-code-line me-1"></i>
                          {t('qrCode.tab')}
                        </NavLink>
                      </NavItem>
                    )}
                  </Nav>
                </CardHeader>
                <CardBody>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <Form className="tablelist-form" onSubmit={(e) => {
                        e.preventDefault();
                        editUserForm.handleSubmit();
                      }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="name-field" className="form-label">{t("Username")}</Label>
                          <Input
                            type="text" id="username" name="username" placeholder={t("Enter Username")}
                            className={`form-control ${editUserForm.touched.username && editUserForm.errors.username ? 'is-invalid' : ''}`}
                            onChange={editUserForm.handleChange}
                            onBlur={editUserForm.handleBlur}
                            value={editUserForm.values.username}/>
                          {editUserForm.touched.username && editUserForm.errors.username && (
                            <div className="invalid-feedback">{editUserForm.errors.username}</div>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">{t("Email")}</Label>
                          <Input
                            type="text" id="email" name="email"
                            className={`form-control ${editUserForm.touched.email && editUserForm.errors.email ? 'is-invalid' : ''}`}
                            placeholder={t("Enter Email")}
                            onChange={editUserForm.handleChange}
                            onBlur={editUserForm.handleBlur}
                            value={editUserForm.values.email || ""}/>
                          {editUserForm.touched.email && editUserForm.errors.email && (
                            <div className="invalid-feedback">{editUserForm.errors.email}</div>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">{t("Password")}</Label>
                          <Input
                            type="text" id="plainPassword" name="plainPassword"
                            className={`form-control ${editUserForm.touched.plainPassword && editUserForm.errors.plainPassword ? 'is-invalid' : ''}`}
                            placeholder={t("Enter Password")}
                            onChange={editUserForm.handleChange}
                            onBlur={editUserForm.handleBlur}
                            value={editUserForm.values.plainPassword || ""}/>
                          {editUserForm.touched.plainPassword && editUserForm.errors.plainPassword && (
                            <div className="invalid-feedback">{editUserForm.errors.plainPassword}</div>
                          )}
                        </div>
                        <div className="text-end">
                          <button type="submit" className="btn btn-primary">{t('Save')}</button>
                        </div>
                      </Form>

                      {/* Blokada jest osobnym endpointem, nie polem formularza —
                          działa od razu, bez zapisywania reszty danych. */}
                      {isAdmin && isClubAdminUser && !isSelf && (
                        <div className="border-top mt-4 pt-3">
                          <div className="d-flex flex-wrap align-items-center gap-3">
                            <div className="flex-grow-1">
                              <h5 className="fs-15 mb-1">
                                {t('account.statusTitle')}{' '}
                                <span className={`badge ${accountEnabled ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                  {accountEnabled ? t('account.active') : t('account.statusBlocked')}
                                </span>
                              </h5>
                              <p className="text-muted mb-0">
                                {accountEnabled ? t('account.blockHint') : t('account.unblockHint')}
                              </p>
                            </div>
                            <Button
                              color={accountEnabled ? 'danger' : 'success'}
                              disabled={enabledSaving}
                              onClick={() => (accountEnabled ? setConfirmBlock(true) : handleSetEnabled(true))}
                            >
                              {enabledSaving ? (
                                <span className="spinner-border spinner-border-sm align-middle me-1" role="status"></span>
                              ) : (
                                <i className={`${accountEnabled ? 'ri-lock-line' : 'ri-lock-unlock-line'} align-bottom me-1`}></i>
                              )}
                              {accountEnabled ? t('account.block') : t('account.unblock')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </TabPane>

                    {isAdmin && (
                      <TabPane tabId="2">
                        {tipsError && (
                          <Alert color="danger">
                            {t('tips.error')}: {typeof tipsError === 'string' ? tipsError : JSON.stringify(tipsError)}
                          </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-3">
                          <Col sm={4} md={3}>
                            <Label className="form-label mb-1" htmlFor="tipStatusFilter">
                              {t('tips.table.status')}
                            </Label>
                            <Input
                              type="select"
                              id="tipStatusFilter"
                              value={statusFilter}
                              onChange={(e) => setStatusFilter(e.target.value)}
                            >
                              <option value="">{t('adminTips.filter.allStatuses')}</option>
                              <option value={STATUS_UNPAID}>{t('tips.status.unpaid')}</option>
                              <option value={STATUS_PAID}>{t('tips.status.paid')}</option>
                            </Input>
                          </Col>
                          <Col>
                            <div className="d-flex flex-wrap gap-4">
                              <div className="text-muted">
                                <span className="fw-semibold">{t('tips.status.unpaid')}:{" "}</span>
                                <span className="text-warning fs-5">{formatAmount(unpaidAmount)}</span>
                              </div>
                              <div className="text-muted">
                                <span className="fw-semibold">{t('tips.status.paid')}:{" "}</span>
                                <span className="text-success fs-5">{formatAmount(paidAmount)}</span>
                              </div>
                              <div className="text-muted">
                                <span className="fw-semibold">{t('tips.header.total')}:{" "}</span>
                                <span className="text-primary fs-5">{formatAmount(totalAmount)}</span>
                              </div>
                            </div>
                          </Col>
                          <Col xs="auto" className="ms-auto">
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                disabled={selectedTipIds.length === 0 || tipsUpdating}
                                onClick={handleMarkSelectedPaid}
                              >
                                <i className="ri-check-line align-bottom me-1"></i>
                                {t('adminTips.markSelected')} ({selectedTipIds.length})
                              </Button>
                              <Button
                                color="primary"
                                outline
                                disabled={unpaidTips.length === 0 || tipsUpdating}
                                onClick={() => setConfirmMarkAll(true)}
                              >
                                <i className="ri-check-double-line align-bottom me-1"></i>
                                {t('adminTips.markAll')}
                              </Button>
                            </div>
                          </Col>
                        </Row>

                        {tipsLoading && tips.length === 0 ? (
                          <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">{t('tips.loading')}</span>
                            </div>
                          </div>
                        ) : filteredTips.length === 0 ? (
                          <div className="text-center py-4">
                            <div className="mb-3">
                              <i className="ri-money-dollar-circle-line display-4 text-muted"></i>
                            </div>
                            <h5 className="text-muted">{t('tips.empty.title')}</h5>
                            <p className="text-muted">{t('adminTips.empty.description')}</p>
                          </div>
                        ) : (
                          <TableContainer
                            columns={tipColumns}
                            data={filteredTips}
                            customPageSize={20}
                            tableClass="table-centered align-middle table-nowrap mb-0"
                            theadClass="text-muted table-light"
                          />
                        )}
                      </TabPane>
                    )}

                    {isAdmin && isClubAdminUser && (
                      <TabPane tabId="3">
                        <Row>
                          <Col lg={5}>
                            <h5 className="fs-15 mb-1">{t('qrCode.title')}</h5>
                            <p className="text-muted mb-3">{t('qrCode.description')}</p>

                            <div className="mb-3">
                              <Label className="form-label mb-1">{t('qrCode.encodedLink')}</Label>
                              <Input type="text" readOnly value={qrEventUrl || ''} />
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                color="success"
                                disabled={qrRegenerating}
                                onClick={() => setConfirmRegenerateQr(true)}
                              >
                                {qrRegenerating ? (
                                  <span className="spinner-border spinner-border-sm align-middle me-1" role="status"></span>
                                ) : (
                                  <i className="ri-refresh-line align-bottom me-1"></i>
                                )}
                                {t('qrCode.regenerate')}
                              </Button>
                              <Button color="primary" outline onClick={handleDownloadQr}>
                                <i className="ri-download-line align-bottom me-1"></i>
                                {t('Download QR Code')}
                              </Button>
                            </div>
                          </Col>
                          <Col lg={4} className="mt-4 mt-lg-0">
                            <div className="border rounded p-3 text-center">
                              <img
                                src={qrSrc}
                                alt={t('qrCode.tab')}
                                className="img-fluid"
                                style={{ maxWidth: '260px' }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </TabPane>
                    )}
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Potwierdzenie oznaczenia wszystkich napiwków */}
      <Modal isOpen={confirmMarkAll} toggle={() => setConfirmMarkAll(false)} className="modal zoomIn" centered>
        <ModalBody>
          <div className="text-center pt-2 fs-15 mx-4 mx-sm-5">
            <p className="text-muted mx-4 mb-1">
              {t('adminTips.confirmAll', { count: unpaidTips.length, amount: formatAmount(unpaidAmount) })}
            </p>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button type="button" className="btn w-sm btn-success" onClick={handleMarkAllPaid}>
              {t('adminTips.confirm')}
            </button>
            <button type="button" className="btn w-sm btn-light" onClick={() => setConfirmMarkAll(false)}>
              {t('Close')}
            </button>
          </div>
        </ModalBody>
      </Modal>

      {/* Blokada odcina club admina natychmiast, także z aktywnym tokenem. */}
      <Modal isOpen={confirmBlock} toggle={() => setConfirmBlock(false)} className="modal zoomIn" centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-lock-line display-5 text-danger"></i>
            <div className="mt-3 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('account.confirmBlockTitle')}</h4>
              <p className="text-muted mx-4 mb-0">{t('account.confirmBlockText', { username: username || '' })}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button type="button" className="btn w-sm btn-danger" onClick={() => handleSetEnabled(false)}>
              {t('account.block')}
            </button>
            <button type="button" className="btn w-sm btn-light" onClick={() => setConfirmBlock(false)}>
              {t('Close')}
            </button>
          </div>
        </ModalBody>
      </Modal>

      {/* Regeneracja nadpisuje plik starego kodu — stąd potwierdzenie. */}
      <Modal isOpen={confirmRegenerateQr} toggle={() => setConfirmRegenerateQr(false)} className="modal zoomIn" centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-qr-code-line display-5 text-warning"></i>
            <div className="mt-3 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('qrCode.confirmTitle')}</h4>
              <p className="text-muted mx-4 mb-0">{t('qrCode.confirmText')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button type="button" className="btn w-sm btn-success" onClick={handleRegenerateQr}>
              {t('qrCode.regenerate')}
            </button>
            <button type="button" className="btn w-sm btn-light" onClick={() => setConfirmRegenerateQr(false)}>
              {t('Close')}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default User;
